import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import api from '../../lib/axios';
import Modal from '../../components/ui/Modal';

export default function StaffServicesModal({ open, member, onClose }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(new Set());

  const { data: allServices } = useQuery({
    queryKey: ['services'],
    queryFn:  () => api.get('/services').then(r => r.data.data),
  });

  const { data: staffServices } = useQuery({
    queryKey: ['staff-services', member?.id],
    queryFn:  () => api.get(`/staff/${member.id}/services`).then(r => r.data.data),
    enabled:  open && !!member?.id,
  });

  useEffect(() => {
    if (staffServices) setSelected(new Set(staffServices.map(s => s.id)));
  }, [staffServices]);

  const mut = useMutation({
    mutationFn: (ids) => api.put(`/staff/${member.id}/services`, { service_ids: ids }),
    onSuccess:  () => { qc.invalidateQueries(['staff']); qc.invalidateQueries(['staff-services', member?.id]); onClose(); },
  });

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const services = (allServices || []).filter(s => s.active);

  function formatPrice(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  return (
    <Modal open={open} onClose={onClose} title={`Services — ${member?.name || ''}`} size="md">
      <p className="text-sm text-ink-secondary mb-4">Select which services this staff member can perform.</p>

      {services.length === 0 ? (
        <p className="text-sm text-ink-muted text-center py-6">No active services found. Add services first.</p>
      ) : (
        <div className="space-y-2">
          {services.map(svc => {
            const on = selected.has(svc.id);
            return (
              <button key={svc.id} type="button" onClick={() => toggle(svc.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all
                  ${on ? 'border-primary/30 bg-primary/5' : 'border-surface-border hover:border-primary/20 hover:bg-surface-50'}`}>
                <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors shrink-0
                  ${on ? 'bg-primary border-primary' : 'border-surface-200'}`}>
                  {on && <Check size={12} className="text-white" />}
                </div>
                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: svc.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">{svc.name}</p>
                  <p className="text-xs text-ink-muted">{svc.duration_minutes} min · {formatPrice(svc.price_cents)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {mut.isError && <p className="text-xs text-red-500 mt-3">{mut.error?.response?.data?.error?.message || 'Save failed'}</p>}

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-surface-border">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={() => mut.mutate([...selected])} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? 'Saving…' : 'Save services'}
        </button>
      </div>
    </Modal>
  );
}
